import type {
  TicketCostBillingLocationTransport,
  TicketCostDetailsTransport,
  TicketCostInputDecisionTransportValidator,
  TicketCostInvoiceTransport,
  TicketCostOcrmTransport,
} from "@ots-frontend/ots-data";
import type {
  TicketCostAdjustment,
  TicketCostBillingLocation,
  TicketCostBucketKey,
  TicketCostInputDecision,
  TicketCostInvoice,
  TicketCostLine,
  TicketCostRateModel,
  TicketCostVersionKind,
} from "./ticket-cost-types";
import {sortTicketCostLines} from "./ticket-cost-types";
import type * as t from "io-ts";

type InputDecisionTransport = t.TypeOf<typeof TicketCostInputDecisionTransportValidator>;

const value = (candidate: number | null | undefined) => candidate ?? 0;
const hasText = (candidate: string | null | undefined): candidate is string => Boolean(candidate?.trim());

const humanize = (candidate: string | null | undefined, fallback = "Charge") => {
  if (!hasText(candidate)) return fallback;
  return candidate
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDate = (timestamp: number | null | undefined) => timestamp
  ? new Intl.DateTimeFormat("en-US", {month: "short", day: "numeric", year: "numeric"}).format(new Date(timestamp))
  : undefined;

const getBucket = (cost: TicketCostOcrmTransport): TicketCostBucketKey => {
  if (value(cost.billableHours) > 0 || value(cost.timeCost) > 0) return "time";
  if (value(cost.freightCost) > 0 || value(cost.freightRate) > 0) return "freight";
  if (value(cost.loadCost) > 0 || value(cost.loadRate) > 0) return "load";
  if (value(cost.taxCost) > 0 || value(cost.taxRate) > 0) return "tax";
  return "specific";
};

const getAmount = (cost: TicketCostOcrmTransport) => cost.totalCost
  ?? cost.timeCost
  ?? cost.freightCost
  ?? cost.loadCost
  ?? cost.taxCost
  ?? 0;

const getQuantity = (cost: TicketCostOcrmTransport, bucket: TicketCostBucketKey) => {
  if (bucket === "time") return cost.billableHours ?? undefined;
  if (bucket === "tax") return cost.taxableAmount ?? undefined;
  if (bucket === "load") return cost.billableQty ?? 1;
  return cost.billableQty ?? undefined;
};

const getRate = (cost: TicketCostOcrmTransport, bucket: TicketCostBucketKey) => {
  if (bucket === "time") return cost.timeRate ?? cost.rateDetailTimeRate ?? undefined;
  if (bucket === "freight") return cost.freightRate ?? cost.rateDetailFreightRate ?? undefined;
  if (bucket === "load") return cost.loadRate ?? cost.rateDetailLoadRate ?? undefined;
  if (bucket === "tax") return cost.taxRate ?? undefined;
  return undefined;
};

const specificAdjustments = (cost: TicketCostOcrmTransport): TicketCostAdjustment[] => [
  ["pickup-demurrage", "Pickup demurrage", cost.pickupDemurrageCost],
  ["delivery-demurrage", "Delivery demurrage", cost.deliveryDemurrageCost],
  ["fuel-base", "Base fuel surcharge", cost.baseFuelSurchargeLoadCost],
  ["fuel-percent", "Percent fuel surcharge", cost.percentFuelSurchargeLoadCost],
  ["site-time", "Site restriction · Time", cost.siteRestrictionTimeCost],
  ["site-freight", "Site restriction · Freight", cost.siteRestrictionFreightCost],
  ["site-load", "Site restriction · Load", cost.siteRestrictionLoadCost],
  ["ticket-reject", "Ticket reject", cost.ticketRejectLoadCost],
  ["shift", "Shift charge", cost.shiftLoadCost],
  ["split-load", "Split load", cost.splitLoadCost],
].flatMap(([id, label, amount]) => typeof amount === "number" && amount !== 0
  ? [{id: `${cost.id ?? "cost"}-${id}`, label: String(label), amount}]
  : []);

const formatInputValue = (input: InputDecisionTransport["systemValue"]) => {
  if (!input) return undefined;
  let rendered: string | undefined;
  if (typeof input.numberValue === "number") rendered = String(input.numberValue);
  else if (hasText(input.textValue)) rendered = input.textValue;
  else if (typeof input.booleanValue === "boolean") rendered = input.booleanValue ? "Yes" : "No";
  else if (typeof input.dttmValue === "number") rendered = formatDate(input.dttmValue);
  else if (hasText(input.refId)) rendered = input.refId;
  return rendered && hasText(input.unitOfMeasure) ? `${rendered} ${input.unitOfMeasure}` : rendered;
};

const normalizeDecision = (decision: string | null | undefined): TicketCostInputDecision["decision"] => {
  const normalized = decision?.toUpperCase();
  if (normalized === "APPLIED" || normalized === "REJECTED" || normalized === "ERROR" || normalized === "IGNORED" || normalized === "REVIEWED") {
    return normalized;
  }
  return "REVIEWED";
};

const mapInputDecisions = (cost: TicketCostOcrmTransport): TicketCostInputDecision[] | undefined => {
  const inputs = cost.costInputs?.inputs;
  if (!inputs) return undefined;
  const decisions = Object.entries(inputs).flatMap(([inputKey, input]) => input ? [{
    inputKey,
    name: humanize(inputKey, "Cost input"),
    decision: normalizeDecision(input.decision),
    rateName: input.rateName ?? undefined,
    rateDetailDescription: input.rateDetailDescription ?? undefined,
    systemValue: formatInputValue(input.systemValue),
    userValue: formatInputValue(input.userValue),
    appliedValue: formatInputValue(input.appliedValue),
    systemCost: input.systemCost ?? undefined,
    candidateCost: input.candidateCost ?? undefined,
    appliedCost: input.appliedCost ?? undefined,
    reason: input.reason ?? input.decisionReason ?? input.reasonCode ?? undefined,
  }] : []);
  return decisions.length ? decisions : undefined;
};

const mapLine = (cost: TicketCostOcrmTransport, index: number): TicketCostLine => {
  const bucket = getBucket(cost);
  const componentCode = cost.costComponentType ?? cost.rateDetailCostComponentType ?? cost.rateCostComponentType;
  const label = hasText(cost.rateDetailDescription)
    ? cost.rateDetailDescription
    : hasText(cost.rateName) ? cost.rateName : humanize(bucket);
  return {
    id: cost.id ?? `${cost.rateModelId ?? "rate-model"}-${cost.rateDetailId ?? "charge"}-${cost.wellId ?? "ticket"}-${index}`,
    label,
    bucket,
    component: hasText(componentCode) ? {code: componentCode, label: humanize(componentCode)} : undefined,
    quantity: getQuantity(cost, bucket),
    quantityLabel: bucket === "time" ? "hours" : undefined,
    billableQuantity: cost.billableQty ?? undefined,
    rate: getRate(cost, bucket),
    rateUnit: bucket === "tax" ? "%" : undefined,
    amount: getAmount(cost),
    wellId: cost.wellId ?? undefined,
    wellName: cost.wellName ?? undefined,
    wellAfe: cost.wellAfe ?? undefined,
    allocation: cost.wellAllocation ?? undefined,
    adjustments: specificAdjustments(cost),
    costInputs: mapInputDecisions(cost),
  };
};

const mapBillingLocation = (location?: TicketCostBillingLocationTransport): TicketCostBillingLocation | undefined => {
  if (!location || (!hasText(location.billingLocationName) && !location.decisionTrace)) return undefined;
  return {
    id: location.billingLocationId ?? undefined,
    name: hasText(location.billingLocationName) ? location.billingLocationName : "No eligible billing location",
    type: hasText(location.billingLocationType) ? humanize(location.billingLocationType) : undefined,
    afe: location.billingAfe ?? undefined,
    afePrefix: location.billingAfePrefix ?? undefined,
    afeSuffix: location.billingAfeSuffix ?? undefined,
    afeSource: location.billingAfeSource ?? undefined,
    afeSourceLabel: location.billingAfeSourceLabel ?? undefined,
    decisionTrace: location.decisionTrace ?? undefined,
  };
};

const normalizeInvoiceStatus = (status: string | null | undefined): TicketCostInvoice["status"] => {
  const normalized = status?.toLowerCase() ?? "";
  if (normalized.includes("void")) return "void";
  if (normalized.includes("paid")) return "paid";
  if (normalized.includes("sent")) return "sent";
  if (normalized.includes("approv")) return "approved";
  return "draft";
};

export const mapTicketCostInvoices = (invoices: TicketCostInvoiceTransport[]): TicketCostInvoice[] => invoices.map((invoice, index) => ({
  id: `${invoice.id ?? "invoice"}-${invoice.invoiceNumber ?? index}-${index}`,
  number: invoice.invoiceNumber ?? "Invoice",
  status: normalizeInvoiceStatus(invoice.status),
  amount: invoice.cost ?? 0,
  invoiceDate: formatDate(invoice.createDttm ?? invoice.fromDttm),
}));

type VersionGroup = {
  key: string;
  versionKind: TicketCostVersionKind;
  timestamp?: number;
  costs: TicketCostOcrmTransport[];
};

const groupVersions = (costs: TicketCostOcrmTransport[], versionKind: "saved" | "preview") => {
  const groups = new Map<string, VersionGroup>();
  costs.forEach((cost) => {
    const modelKey = cost.rateModelId ?? cost.rateModelName ?? "unknown-rate-model";
    const historical = versionKind === "saved" && typeof cost.historicalCreateDttm === "number";
    const kind: TicketCostVersionKind = versionKind === "preview" ? "preview" : historical ? "historical" : "current";
    const marker = historical ? String(cost.historicalCreateDttm) : kind;
    const key = `${modelKey}:${marker}`;
    const group = groups.get(key) ?? {key, versionKind: kind, timestamp: cost.historicalCreateDttm ?? undefined, costs: []};
    group.costs.push(cost);
    groups.set(key, group);
  });
  return Array.from(groups.values());
};

export const mapTicketCostRateModels = (
  details: TicketCostDetailsTransport,
  invoices: TicketCostInvoice[],
  financialLabel: string,
): TicketCostRateModel[] => {
  const groups = [...groupVersions(details.saved, "saved"), ...groupVersions(details.preview, "preview")];
  const currentTotals = new Map<string, number>();
  groups.filter((group) => group.versionKind === "current").forEach((group) => {
    const first = group.costs[0];
    currentTotals.set(first?.rateModelId ?? first?.rateModelName ?? "unknown-rate-model", group.costs.reduce((sum, cost) => sum + getAmount(cost), 0));
  });

  return groups.map((group) => {
    const first = group.costs[0] ?? {};
    const rateModelKey = first.rateModelId ?? first.rateModelName ?? "unknown-rate-model";
    const lines = sortTicketCostLines(group.costs.map(mapLine));
    const total = lines.reduce((sum, line) => sum + line.amount, 0);
    const bucketTotals = new Map<TicketCostBucketKey, number>();
    lines.forEach((line) => bucketTotals.set(line.bucket, (bucketTotals.get(line.bucket) ?? 0) + line.amount));
    const allocations = new Map<string, {id: string; wellName: string; allocation: number; amount: number; lineCount: number; afe?: string}>();
    lines.filter((line) => line.wellId || line.wellName).forEach((line) => {
      const id = line.wellId ?? line.wellName ?? "unknown-well";
      const existing = allocations.get(id) ?? {id, wellName: line.wellName ?? "Unknown well", allocation: line.allocation ?? 0, amount: 0, lineCount: 0, afe: line.wellAfe};
      existing.amount += line.amount;
      existing.lineCount += 1;
      if (line.allocation !== undefined) existing.allocation = line.allocation;
      allocations.set(id, existing);
    });
    const calculatedAt = group.versionKind === "historical"
      ? group.timestamp
      : Math.max(...group.costs.map((cost) => cost.lastModifiedDttm ?? cost.createDttm ?? 0));
    const historicalDate = formatDate(group.timestamp);

    return {
      id: group.key,
      name: first.rateModelName ?? "Rate model",
      rateModelKey,
      rateModelLabel: first.rateModelName ?? "Rate model",
      versionKind: group.versionKind,
      versionOptionLabel: group.versionKind === "preview" ? "Preview · unposted data" : group.versionKind === "historical" ? `Saved · ${historicalDate ?? "history"}` : "Current · posted",
      versionLabel: group.versionKind === "preview" ? "Preview" : group.versionKind === "historical" ? "Historical" : "Current",
      historical: group.versionKind === "historical",
      company: first.rateModelCompanyName ?? undefined,
      modelType: hasText(first.rateModelType) ? humanize(first.rateModelType) : undefined,
      financialLabel,
      total,
      calculatedAt: calculatedAt > 0 ? formatDate(calculatedAt) : undefined,
      billingLocationDetails: mapBillingLocation(details.billingLocation),
      preview: group.versionKind === "preview" ? {
        sourceLabel: "Calculated from the ticket's current unposted values",
        comparisonLabel: "posted",
        comparisonTotal: currentTotals.get(rateModelKey),
        notice: "This estimate has not been posted.",
      } : undefined,
      buckets: (["time", "freight", "load", "tax", "specific"] as TicketCostBucketKey[])
        .map((key) => ({key, label: humanize(key), amount: bucketTotals.get(key) ?? 0})),
      lines,
      allocations: allocations.size ? Array.from(allocations.values()).sort((left, right) =>
        left.wellName.localeCompare(right.wellName) || left.id.localeCompare(right.id)) : undefined,
      invoices,
    };
  }).sort((left, right) => {
    if (left.rateModelKey !== right.rateModelKey) return (left.rateModelLabel ?? "").localeCompare(right.rateModelLabel ?? "");
    const order: Record<TicketCostVersionKind, number> = {current: 0, preview: 1, historical: 2};
    return order[left.versionKind ?? "current"] - order[right.versionKind ?? "current"];
  });
};
